import React, { useEffect, useState, useCallback, useMemo } from 'react';
import NavHeader from '../components/NavHeader';
import {
  fetchPayingBanks,
  createPayingBank,
  updatePayingBank,
  deletePayingBank,
} from '../api';

// Management UI for the client-owned paying banks that appear in the EFT
// Request Form's "Transfer funds from" section. Mirrors VendorsAdmin's
// structure/styling. Reads are open to any authenticated user; create/edit/
// delete are processor/admin-only — enforced server-side, so no client-side
// gating here (matching VendorsAdmin).
//
// Deleting (or editing) a bank never changes already-generated packages:
// each batch keeps its own snapshot of the selected bank's fields.

const EMPTY_FORM = {
  entity_name: '',
  company_code: '',
  bank_name: '',
  bank_address: '',
  account_number: '',
  iban: '',
  swift_code: '',
  sort_code: '',
  currency: '',
  active: true,
};

// Nullable string columns: blank inputs are sent as explicit nulls on save.
const NULLABLE_FIELDS = [
  'company_code',
  'bank_name',
  'bank_address',
  'account_number',
  'iban',
  'swift_code',
  'sort_code',
  'currency',
];

function PayingBanksAdmin() {
  const [banks, setBanks] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Modal state — null = closed, 'add' = new bank, UUID = editing that bank
  const [modalMode, setModalMode] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadBanks = useCallback(async () => {
    try {
      const res = await fetchPayingBanks();
      const data = res.data;
      setBanks(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load paying banks');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadBanks();
  }, [loadBanks]);

  const filteredBanks = useMemo(() =>
    banks
      .filter((b) => {
        const q = search.toLowerCase();
        return (
          (b.entity_name || '').toLowerCase().includes(q) ||
          (b.bank_name || '').toLowerCase().includes(q) ||
          (b.company_code || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (a.entity_name || '').localeCompare(b.entity_name || '')),
    [banks, search]
  );

  const openAdd = () => {
    setFormData({ ...EMPTY_FORM });
    setModalMode('add');
  };

  const openEdit = (bank) => {
    setFormData({
      entity_name: bank.entity_name || '',
      company_code: bank.company_code || '',
      bank_name: bank.bank_name || '',
      bank_address: bank.bank_address || '',
      account_number: bank.account_number || '',
      iban: bank.iban || '',
      swift_code: bank.swift_code || '',
      sort_code: bank.sort_code || '',
      currency: bank.currency || '',
      active: bank.active !== false,
    });
    setModalMode(bank.id);
  };

  const closeModal = () => {
    setModalMode(null);
    setFormData({ ...EMPTY_FORM });
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    const payload = { entity_name: formData.entity_name.trim(), active: formData.active };
    for (const field of NULLABLE_FIELDS) {
      const value = (formData[field] || '').trim();
      payload[field] = value || null;
    }
    return payload;
  };

  const handleSave = async () => {
    if (!formData.entity_name.trim()) return;
    setSaving(true);
    try {
      if (modalMode === 'add') {
        await createPayingBank(buildPayload());
        showToast('Paying bank created successfully', 'success');
      } else {
        await updatePayingBank(modalMode, buildPayload());
        showToast('Paying bank updated successfully', 'success');
      }
      closeModal();
      loadBanks();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save paying bank');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, bankId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this paying bank? Already-generated packages keep their own copy of the bank details.')) return;
    try {
      await deletePayingBank(bankId);
      showToast('Paying bank deleted', 'success');
      loadBanks();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete paying bank');
    }
  };

  return (
    <div className="projects-page">
      <NavHeader actions={
        <button className="btn btn-primary" onClick={openAdd}>Add Paying Bank</button>
      } />

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Search */}
      <div className="projects-search">
        <input
          type="text"
          placeholder="Search by entity, bank name, or company code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
        {search && (
          <span className="search-count">{filteredBanks.length} of {banks.length} paying banks</span>
        )}
      </div>

      {/* Paying Banks Table */}
      <div className="projects-table-container">
        {loading ? (
          <div className="loading-spinner">Loading paying banks...</div>
        ) : (
          <>
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Company Code</th>
                  <th>Bank Name</th>
                  <th>Account #</th>
                  <th>IBAN</th>
                  <th>Currency</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanks.map((b) => (
                  <tr
                    key={b.id}
                    className="clickable-row"
                    onClick={() => openEdit(b)}
                  >
                    <td>{b.entity_name}</td>
                    <td>{b.company_code || '—'}</td>
                    <td>{b.bank_name || '—'}</td>
                    <td>{b.account_number || '—'}</td>
                    <td>{b.iban || '—'}</td>
                    <td>{b.currency || '—'}</td>
                    <td>{b.active ? 'Yes' : 'No'}</td>
                    <td className="project-actions">
                      <button
                        className="btn btn-reject btn-sm"
                        onClick={(e) => handleDelete(e, b.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredBanks.length === 0 && search && (
              <div className="projects-empty">No matching paying banks.</div>
            )}
            {banks.length === 0 && !search && (
              <div className="projects-empty">No paying banks yet. Click "Add Paying Bank" above.</div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {modalMode !== null && (
        <div className="project-modal-overlay" onClick={closeModal}>
          <div className="project-modal project-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="project-modal-header">
              <h2>{modalMode === 'add' ? 'New Paying Bank' : 'Edit Paying Bank'}</h2>
              <button className="project-modal-close" onClick={closeModal}>&times;</button>
            </div>

            <div className="project-modal-body">
              <div className="project-modal-row">
                <label>Entity Name *</label>
                <input
                  type="text"
                  value={formData.entity_name}
                  onChange={(e) => setField('entity_name', e.target.value)}
                  placeholder="e.g. Mast Industries UK Limited"
                  autoFocus
                />
              </div>

              <div className="project-modal-row-group">
                <div className="project-modal-row">
                  <label>Company Code</label>
                  <input
                    type="text"
                    value={formData.company_code}
                    onChange={(e) => setField('company_code', e.target.value)}
                    placeholder="e.g. 1256"
                  />
                </div>
                <div className="project-modal-row">
                  <label>Currency</label>
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={(e) => setField('currency', e.target.value)}
                    placeholder="e.g. GBP, EUR"
                    maxLength={3}
                  />
                </div>
              </div>

              <div className="project-modal-row">
                <label>Bank Name</label>
                <input
                  type="text"
                  value={formData.bank_name}
                  onChange={(e) => setField('bank_name', e.target.value)}
                  placeholder="Bank name"
                />
              </div>

              <div className="project-modal-row">
                <label>Bank Address</label>
                <input
                  type="text"
                  value={formData.bank_address}
                  onChange={(e) => setField('bank_address', e.target.value)}
                  placeholder="Bank address"
                />
              </div>

              <div className="project-modal-row-group">
                <div className="project-modal-row">
                  <label>Account Number</label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setField('account_number', e.target.value)}
                    placeholder="Account number"
                  />
                </div>
                <div className="project-modal-row">
                  <label>IBAN</label>
                  <input
                    type="text"
                    value={formData.iban}
                    onChange={(e) => setField('iban', e.target.value)}
                    placeholder="IBAN"
                  />
                </div>
              </div>

              <div className="project-modal-row-group">
                <div className="project-modal-row">
                  <label>SWIFT Code</label>
                  <input
                    type="text"
                    value={formData.swift_code}
                    onChange={(e) => setField('swift_code', e.target.value)}
                    placeholder="SWIFT / BIC"
                  />
                </div>
                <div className="project-modal-row">
                  <label>Sort Code</label>
                  <input
                    type="text"
                    value={formData.sort_code}
                    onChange={(e) => setField('sort_code', e.target.value)}
                    placeholder="Sort code"
                  />
                </div>
              </div>

              <div className="project-modal-row">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setField('active', e.target.checked)}
                  />
                  Active (shown in the "Transfer funds from" dropdown)
                </label>
              </div>
            </div>

            <div className="project-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!formData.entity_name.trim() || saving}
              >
                {saving ? 'Saving...' : (modalMode === 'add' ? 'Create Paying Bank' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayingBanksAdmin;
